import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { ArrowLeft, Settings, UserPlus, Trash2, Key, Shield, Crown, Mail, Send, Plus, X, CreditCard } from 'lucide-react';
import { Switch } from '../../components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newAdminForm, setNewAdminForm] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  // Daily stats email config
  const [dailyStatsEmails, setDailyStatsEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [savingEmails, setSavingEmails] = useState(false);
  
  // Payment settings
  const [paymentSettings, setPaymentSettings] = useState({
    auto_mark_paid_on_stripe_payment: true
  });
  const [savingPaymentSettings, setSavingPaymentSettings] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchPaymentSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/payment`, {
        headers: getAuthHeaders()
      });
      setPaymentSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch payment settings');
    }
  }, [getAuthHeaders]);

  const updatePaymentSettings = async (updates) => {
    setSavingPaymentSettings(true);
    try {
      const newSettings = { ...paymentSettings, ...updates };
      await axios.put(`${API}/admin/settings/payment`, newSettings, {
        headers: getAuthHeaders()
      });
      setPaymentSettings(newSettings);
      toast.success('Payment settings updated');
    } catch (error) {
      toast.error('Failed to update payment settings');
    } finally {
      setSavingPaymentSettings(false);
    }
  };

  const fetchDailyStatsConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/daily-stats/config`, {
        headers: getAuthHeaders()
      });
      setDailyStatsEmails(response.data.emails || []);
    } catch (error) {
      console.error('Failed to fetch daily stats config');
    }
  }, [getAuthHeaders]);

  const fetchAdmins = useCallback(async () => {
    try {
      const [adminsRes, meRes] = await Promise.all([
        axios.get(`${API}/admin/admins`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/me`, { headers: getAuthHeaders() })
      ]);
      setAdmins(adminsRes.data.admins || []);
      setCurrentAdmin(meRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      } else {
        toast.error('Failed to fetch admin data');
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, navigate]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchAdmins();
    fetchDailyStatsConfig();
    fetchPaymentSettings();
  }, [navigate, fetchAdmins, fetchDailyStatsConfig, fetchPaymentSettings]);

  const handleAddEmailToList = () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    if (dailyStatsEmails.includes(newEmail)) {
      toast.error('Email already added');
      return;
    }
    setDailyStatsEmails(prev => [...prev, newEmail]);
    setNewEmail('');
  };

  const handleRemoveEmail = (email) => {
    setDailyStatsEmails(prev => prev.filter(e => e !== email));
  };

  const handleSaveDailyStatsEmails = async () => {
    setSavingEmails(true);
    try {
      await axios.post(`${API}/admin/daily-stats/config`, {
        admin_emails: dailyStatsEmails
      }, { headers: getAuthHeaders() });
      toast.success('Daily stats email list saved');
    } catch (error) {
      toast.error('Failed to save email config');
    } finally {
      setSavingEmails(false);
    }
  };

  const handleSendTestEmail = async () => {
    setSendingTestEmail(true);
    try {
      const response = await axios.post(`${API}/admin/daily-stats/send`, {}, {
        headers: getAuthHeaders()
      });
      toast.success(`Test email sent to ${response.data.recipients?.length || 0} recipients`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdminForm.email || !newAdminForm.password || !newAdminForm.name) {
      toast.error('All fields are required');
      return;
    }

    try {
      await axios.post(`${API}/admin/admins`, newAdminForm, {
        headers: getAuthHeaders()
      });
      toast.success('Admin added');
      setShowAddModal(false);
      setNewAdminForm({ email: '', password: '', name: '' });
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add admin');
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm('Delete this admin?')) return;

    try {
      await axios.delete(`${API}/admin/admins/${adminId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Admin deleted');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete admin');
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await axios.post(`${API}/admin/reset-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, {
        headers: getAuthHeaders()
      });
      
      toast.success('Password changed successfully');
      setShowPasswordModal(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const isGodAdmin = currentAdmin?.role === 'god_admin';

  return (
    <>
      <meta name="robots" content="noindex, nofollow" />
      
      <div className="min-h-screen bg-slate-900">
        {/* Header */}
        <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <div className="flex items-center gap-2">
              <Settings className="w-6 h-6 text-red-500" />
              <h1 className="text-xl font-bold text-white">Admin Settings</h1>
            </div>
          </div>
        </header>

        <main className="p-6">
          <div className="max-w-4xl mx-auto space-y-8">
            {/* My Account Section */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
              <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-500" />
                My Account
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                  <div>
                    <p className="font-medium text-white">{currentAdmin?.name}</p>
                    <p className="text-sm text-slate-400">{currentAdmin?.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {currentAdmin?.role === 'god_admin' && (
                      <span className="bg-amber-600 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                        <Crown className="w-3 h-3" />
                        GOD ADMIN
                      </span>
                    )}
                  </div>
                </div>

                <Button
                  onClick={() => setShowPasswordModal(true)}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  <Key className="w-4 h-4 mr-2" />
                  Change Password
                </Button>
              </div>
            </div>

            {/* Admin Users Section */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-red-500" />
                  Admin Users
                </h2>
                {isGodAdmin && (
                  <Button
                    onClick={() => setShowAddModal(true)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Admin
                  </Button>
                )}
              </div>

              <div className="space-y-3">
                {admins.map(admin => (
                  <div 
                    key={admin.id}
                    className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-slate-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold">
                          {admin.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-white">{admin.name}</p>
                        <p className="text-sm text-slate-400">{admin.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {admin.role === 'god_admin' ? (
                        <span className="bg-amber-600 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                          <Crown className="w-3 h-3" />
                          GOD
                        </span>
                      ) : (
                        <span className="bg-slate-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                          ADMIN
                        </span>
                      )}
                      {isGodAdmin && admin.role !== 'god_admin' && admin.id !== currentAdmin?.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteAdmin(admin.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {!isGodAdmin && (
                <p className="text-slate-500 text-sm mt-4">
                  Only God Admin can add or remove other admins.
                </p>
              )}
            </div>

            {/* Payment Settings Section */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
              <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-purple-500" />
                Payment Settings
              </h2>
              
              <p className="text-slate-400 text-sm mb-6">
                Configure how job payments are handled when customers pay through Stripe.
              </p>
              
              <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                <div>
                  <p className="font-medium text-white">Auto-mark jobs as "Paid"</p>
                  <p className="text-slate-400 text-sm">
                    When enabled, jobs will automatically be marked as "Paid" when customers complete Stripe payment. 
                    When disabled, payments will be recorded but you'll need to manually mark jobs as paid.
                  </p>
                </div>
                <Switch
                  checked={paymentSettings.auto_mark_paid_on_stripe_payment}
                  onCheckedChange={(checked) => updatePaymentSettings({ auto_mark_paid_on_stripe_payment: checked })}
                  disabled={savingPaymentSettings}
                />
              </div>
              
              <div className="mt-4 p-4 bg-purple-900/30 border border-purple-700/50 rounded-lg">
                <p className="text-purple-300 text-sm">
                  <strong>Job Status Flow:</strong> Quote → Scheduled → Invoiced (auto when payment link sent) → Paid
                </p>
              </div>
            </div>

            {/* Daily Stats Email Section */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
              <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                <Mail className="w-5 h-5 text-emerald-500" />
                Daily Stats Email
              </h2>
              
              <p className="text-slate-400 text-sm mb-6">
                Get a daily summary with new signups, MRR changes, and active users sent at 8 AM Central.
              </p>
              
              {/* Simple Email Input */}
              <div className="mb-4">
                <Label className="text-slate-300 text-sm mb-2 block">Email Recipients</Label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="Enter email address"
                    className="bg-slate-700 border-slate-600 flex-1 text-white"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddEmailToList();
                      }
                    }}
                  />
                  <Button
                    onClick={handleAddEmailToList}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
              </div>

              {/* Email Tags */}
              {dailyStatsEmails.length > 0 ? (
                <div className="flex flex-wrap gap-2 mb-6">
                  {dailyStatsEmails.map((email, index) => (
                    <div 
                      key={index} 
                      className="flex items-center gap-2 px-3 py-2 bg-slate-700 rounded-full border border-slate-600"
                    >
                      <span className="text-slate-200 text-sm">{email}</span>
                      <button
                        onClick={() => handleRemoveEmail(email)}
                        className="text-slate-400 hover:text-red-400 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-slate-500 text-sm p-4 bg-slate-700/30 rounded-lg mb-6 text-center">
                  No emails configured yet. Add an email above to receive daily stats.
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={handleSaveDailyStatsEmails}
                  disabled={savingEmails}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {savingEmails ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  onClick={handleSendTestEmail}
                  disabled={sendingTestEmail || dailyStatsEmails.length === 0}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sendingTestEmail ? 'Sending...' : 'Send Test Now'}
                </Button>
              </div>
              
              <p className="text-slate-500 text-xs mt-4">
                Emails are sent daily at 8 AM Central Time (automatic).
              </p>
            </div>
          </div>
        </main>

        {/* Add Admin Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle>Add Admin User</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-slate-400">Name</Label>
                <Input
                  value={newAdminForm.name}
                  onChange={(e) => setNewAdminForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Admin name"
                  className="bg-slate-700 border-slate-600"
                />
              </div>

              <div>
                <Label className="text-slate-400">Email</Label>
                <Input
                  type="email"
                  value={newAdminForm.email}
                  onChange={(e) => setNewAdminForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="admin@example.com"
                  className="bg-slate-700 border-slate-600"
                />
              </div>

              <div>
                <Label className="text-slate-400">Password</Label>
                <Input
                  type="password"
                  value={newAdminForm.password}
                  onChange={(e) => setNewAdminForm(p => ({ ...p, password: e.target.value }))}
                  placeholder="••••••••"
                  className="bg-slate-700 border-slate-600"
                />
              </div>

              <Button onClick={handleAddAdmin} className="w-full bg-red-600 hover:bg-red-700">
                Add Admin
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Change Password Modal */}
        <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle>Change Password</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-slate-400">Current Password</Label>
                <Input
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm(p => ({ ...p, current_password: e.target.value }))}
                  className="bg-slate-700 border-slate-600"
                />
              </div>

              <div>
                <Label className="text-slate-400">New Password</Label>
                <Input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm(p => ({ ...p, new_password: e.target.value }))}
                  className="bg-slate-700 border-slate-600"
                />
              </div>

              <div>
                <Label className="text-slate-400">Confirm New Password</Label>
                <Input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm(p => ({ ...p, confirm_password: e.target.value }))}
                  className="bg-slate-700 border-slate-600"
                />
              </div>

              <Button onClick={handleChangePassword} className="w-full bg-red-600 hover:bg-red-700">
                Change Password
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
